import {
  InternalTransferMethodEnum,
  InternalTransferPayload,
  InternalTransferTypeEnum,
  ISubmit,
} from '@interfaces';
import { CostBasisPosition } from '@interfaces/cost-basis/cost-basis.service.interface';
import {
  InternalTransferFrom,
  InternalTransferTo,
  InternalTransferTriage,
} from '@interfaces/state';

import { AccountTypes } from './account-types';
import { CostBasisMethods } from './cost-basis-methods';
import { FlowTypes } from './flow-types';
import { GiftRecipientTypes, YesNoEmpty } from './gift-recipient-types';
import { GiftingDesignationTypes } from './gifting-designation-types';
import { JointGiftingApprovalDBPayload } from './joint-gifting-approval';
import { MultiSignerSignState } from './mxm-sign-state.enum';
import { TransactionTypesEnum } from './selected-client-accounts.interface';

export interface MXMPosition {
  positionId: string; //same as holdingIdentifier
  ticker?: string;
  selectedAmount: string;
  giftingMethod?: string;
  selectedCostBasisMethod?: CostBasisMethods;
  shownCostBasisMethod?: CostBasisMethods;
  sharesToTransfer?: string;
  percentOfHolding?: string;
  taxLots?: MXMTaxLot[];
  additionalTaxLotId?: string;
  estValueOfTransfer?: string;
}

export interface MXMTaxLot {
  lotID: string;
  quantity: number;
}

export interface MultiSignerTransferFrom {
  accountNumber: string;
  accountType: string;
  accountName: string;
  accountId: string;
  transactionType?: TransactionTypesEnum;
  transferType?: InternalTransferTypeEnum;
  transferMethod?: InternalTransferMethodEnum;
  convertAvgCostHoldings?: boolean;
  convertSpecIDHoldings?: boolean;
  percentageOfHoldings?: number;
}

export interface MultiSignerTransferTo {
  //combines Account and Recipient
  accountNumber: string;
  accountType?: string;
  accountName?: string; //-> name for recipient
  anonymousGift?: YesNoEmpty;
  dtcNumber?: string;
  giftRecipientType?: GiftRecipientTypes; // -> type for recipient
  firm?: string;
  transferAgentName?: string;
}

export interface MultiSignerTransferToWithPositions extends MultiSignerTransferTo {
  positions: MXMPosition[];
}

interface MultiSignerPostPayloadBase {
  triage: MXMTriage;
  transferFrom: MultiSignerTransferFrom;
  metricsCorrelationID: string;
}

export interface MultiSignerPostPayloadSingle extends MultiSignerPostPayloadBase {
  transferTo: MultiSignerTransferTo;
  positions: MXMPosition[];
  // Filenet submission for SingleIT
  filenet: ISubmit;
  transferToAccounts?: never;
}

export interface MultiSignerPostPayloadMulti extends MultiSignerPostPayloadBase {
  transferToAccounts: MultiSignerTransferToWithPositions[];
  // Filenet submissions for MultiIT
  filenet: InternalTransferPayload[];
  transferTo?: never;
  positions?: never;
}

export type MultiSignerPostPayload = MultiSignerPostPayloadSingle | MultiSignerPostPayloadMulti;

export enum TransferStatus {
  CREATED = 'CREATED', // Indicates that a transfer has been created, but confirmation and signer emails haven't been sent
  PENDING = 'PENDING', // Indicates confirmation and signer emails have been sent, and transfer is awaiting signers
  REMINDED = 'REMINDED', // Indicates that reminder emails have been sent to signers who haven't signed
  COMPLETE = 'COMPLETE', // Terminal State: Indicates that a transfer has been completed successfully
  CANCELED = 'CANCELED', // Terminal State: Indicates that a transfer has been canceled due to expiration and the cancellation email has been sent (from housekeeping)
}

export interface HydratedTransferToDestination {
  transferTo: InternalTransferTo;
  selectedPositions: CostBasisPosition[];
}

interface MultiSignerGetResponseHydrated_IT_Base {
  transferId: string;
  transactionType: TransactionTypesEnum;
  signState: MultiSignerSignState;
  triage: InternalTransferTriage;
  transferFrom: InternalTransferFrom;
  status: TransferStatus;
  metricsCorrelationID?: string;
}

export interface MultiSignerGetResponseHydrated_IT_Single
  extends MultiSignerGetResponseHydrated_IT_Base {
  transferTo: InternalTransferTo;
  selectedPositions: CostBasisPosition[];
  transferToAccounts?: never;
}

export interface MultiSignerGetResponseHydrated_IT_Multi
  extends MultiSignerGetResponseHydrated_IT_Base {
  transferToAccounts: HydratedTransferToDestination[];
  transferTo?: never;
  selectedPositions?: never;
}

export type MultiSignerGetResponseHydrated_IT =
  | MultiSignerGetResponseHydrated_IT_Single
  | MultiSignerGetResponseHydrated_IT_Multi;

export interface MultiSignerGetResponseHydrated_Gift extends JointGiftingApprovalDBPayload {
  transactionType: FlowTypes;
  transferId: string;
  signState: MultiSignerSignState;
  status: TransferStatus;
  metricsCorrelationID?: string;
}

export interface MXMTriage {
  transferFrom: AccountTypes; //accountTypeInheriting for gifting
  transferTo: AccountTypes;
  isOwner?: boolean; //false by default for gifting, true by default for IT
  exceptionsApply?: boolean; //false by default for gifting, true by default for IT
  flowType?: FlowTypes;
  giftingDesignation?: GiftingDesignationTypes;
}

export interface MXMSigner {
  poid: number | string;
  signState: MultiSignerSignState;
  owner: boolean;
}

//Fiddle with this when we tinker with the GET CWP BE Endpoint
export interface MXMGetResponse {
  triage: MXMTriage;
  // Come back and fix these when the interface names get updated
  transferFrom: MultiSignerTransferFrom;
  transferTo: MultiSignerTransferTo;
  positions: MXMPosition[];
  transferId: string;
  signers: MXMSigner[];
  tStatus: TransferStatus;
}

export interface MXMPostResponse {
  status: number;
  data: {
    transferId: string;
  };
}

export interface MXMPatchResponse {
  signed: boolean;
  allSigned: boolean;
}

export function isMultiPostPayload(
  payload: MultiSignerPostPayload,
): payload is MultiSignerPostPayloadMulti {
  return Array.isArray(payload.transferToAccounts);
}

export function isMultiHydratedIT(
  response: MultiSignerGetResponseHydrated_IT,
): response is MultiSignerGetResponseHydrated_IT_Multi {
  return Array.isArray(response.transferToAccounts);
}
